<template>
  <div
    class="group/input"
    style="width: 100%"
    :class="{
      'flex justify-between': flex,
    }"
  >
    <t-label v-if="label" :disabled="disabled">{{ label }}</t-label>
    <div ref="outsideAutoComplete" class="relative">
      <input
        ref="inputRef"
        v-model="localSearch"
        class="block w-full rounded-md border border-solid border-transparent bg-gray-600 px-2.5 py-[7px] font-inter text-xs font-medium leading-[16px] text-gray-50 transition duration-300 placeholder:text-gray-100 focus:!border-transparent focus:bg-gray-700 focus:!text-gray-10 focus:outline focus:outline-2 focus:outline-accent-1 disabled:!cursor-not-allowed disabled:!border-transparent disabled:!bg-gray-600 disabled:!text-gray-200 group-hover/input:border-gray-400"
        :class="{
          '!outline !outline-1 !outline-destructive-2 focus:!outline-destructive-2':
            errorInput,
        }"
        type="text"
        :placeholder="placeholder"
        :disabled="disabled"
        autocomplete="off"
        @focus="onFocus"
        @input="onInput"
        @blur="$emit('on-blur')"
        @keydown.enter="$emit('enter-pressed')"
      />

      <ul
        v-if="showDropdown"
        class="t-autocomplete-list absolute left-0 top-full z-10 mt-1 max-h-[200px] w-full list-none overflow-hidden rounded-md border border-solid border-gray-400 bg-gray-600 p-0"
      >
        <PerfectScrollbar
          :style="{ height: calculatedScrollHeight + 'px' }"
          :options="{ wheelPropagation: false }"
          @ps-y-reach-end="loadMore"
        >
          <li
            v-for="(option, index) in filteredData"
            :key="index"
            class="cursor-pointer px-3 py-1 font-inter text-xs font-medium leading-6 text-gray-100 transition duration-300 hover:bg-gray-400 hover:text-gray-50"
            @click="selectionChanged(option)"
          >
            <p
              v-tooltip.bottom-end="{
                content: getTooltipContent(getOptionLabel(option)),
                delay: { show: 300 },
              }"
              class="m-0 truncate p-0"
            >
              {{ getOptionLabel(option) }}
            </p>
          </li>
          <li
            v-if="localLoading || loading"
            class="flex justify-center px-3 py-1"
          >
            <t-loading-spinner></t-loading-spinner>
          </li>
        </PerfectScrollbar>
      </ul>

      <div
        v-if="errorInput && errorText !== ''"
        class="font-regular mt-1 flex items-start gap-[5px] font-inter text-xs leading-[16px] text-destructive-2"
      >
        <div class="h-5 w-5">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 2.75C13.8555 2.75 17 5.89453 17 9.75C17 13.6328 13.8555 16.75 10 16.75C6.11719 16.75 3 13.6328 3 9.75C3 5.89453 6.11719 2.75 10 2.75ZM10 15.4375C13.1172 15.4375 15.6875 12.8945 15.6875 9.75C15.6875 6.63281 13.1172 4.0625 10 4.0625C6.85547 4.0625 4.3125 6.63281 4.3125 9.75C4.3125 12.8945 6.85547 15.4375 10 15.4375ZM10 11.0625C9.61719 11.0625 9.34375 10.7891 9.34375 10.4062V6.90625C9.34375 6.55078 9.61719 6.25 10 6.25C10.3555 6.25 10.6562 6.55078 10.6562 6.90625V10.4062C10.6562 10.7891 10.3555 11.0625 10 11.0625ZM10 11.9922C10.4648 11.9922 10.8477 12.375 10.8477 12.8398C10.8477 13.3047 10.4648 13.6875 10 13.6875C9.50781 13.6875 9.125 13.3047 9.125 12.8398C9.125 12.375 9.50781 11.9922 10 11.9922Z"
              fill="currentColor"
            />
          </svg>
        </div>
        <span class="inline-flex min-h-[20px] items-center">{{
          errorText
        }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import TLabel from "../TLabel/TLabel.vue";
import TLoadingSpinner from "../TLoadingSpinner/TLoadingSpinner.vue";
import { PerfectScrollbar } from "vue3-perfect-scrollbar";
import "vue3-perfect-scrollbar/style.css";
import { vTooltip } from "floating-vue";
import "floating-vue/dist/style.css";
import { onClickOutside } from "@vueuse/core";
import { ref, computed, useTemplateRef } from "vue";

import { IData } from "../../types/AutoComplete/IData";

type Option = string | IData;

const props = withDefaults(
  defineProps<{
    modelValue: string;
    data: Option[];
    label?: string;
    truncateLength?: number;
    disabled?: boolean;
    loadMoreFnc?: (page: number) => Promise<void> | void;
    totalRecords?: number;
    loading?: boolean;
    placeholder?: string;
    errorInput?: boolean;
    errorText?: string;
    flex?: boolean;
    /**
     * Key used to read the label/value from object-shaped (IData) options.
     * Only relevant when `data` contains objects.
     */
    dataKey?: string;
    selected?: string;
    labeNoDataFound?: string;
  }>(),
  {
    truncateLength: 50,
    dataKey: "name",
    labeNoDataFound: "No data found",
  },
);

const emit = defineEmits([
  "update:modelValue",
  "selection-changed",
  "enter-pressed",
  "on-blur",
]);

const opened = ref(false);
const page = ref(0);
const localLoading = ref(false);
const outsideAutoComplete = useTemplateRef("outsideAutoComplete");

const localSearch = computed({
  get() {
    const doc = new DOMParser().parseFromString(props.modelValue, "text/html");
    return doc.body.textContent || "";
  },
  set(val: string) {
    emit("update:modelValue", val);
  },
});

function getOptionLabel(option: Option): string {
  if (typeof option === "string") {
    return option;
  }
  return option[props.dataKey] ?? "";
}

// Server-paged mode (object data with loadMoreFnc/totalRecords) shows the data
// as provided by the parent; string data is filtered client-side.
const isServerMode = computed(
  () => props.loadMoreFnc !== undefined || props.totalRecords !== undefined,
);

const filteredData = computed<Option[]>(() => {
  if (isServerMode.value || !localSearch.value) {
    return props.data;
  }
  const searchLower = localSearch.value.toLowerCase();
  return props.data.filter((item) =>
    getOptionLabel(item).toLowerCase().includes(searchLower),
  );
});

const showDropdown = computed(() => {
  return opened.value && filteredData.value.length > 0;
});

const calculatedScrollHeight = computed(() => {
  const count = filteredData.value.length;
  if (count < 6) {
    return count * 32;
  }
  return 200;
});

function selectionChanged(option: Option) {
  emit("selection-changed", option);
  emit("update:modelValue", getOptionLabel(option));
  opened.value = false;
}

function onFocus() {
  if (!props.disabled) {
    opened.value = true;
  }
}

function onInput() {
  if (!props.disabled) {
    opened.value = true;
  }
}

onClickOutside(outsideAutoComplete, () => {
  opened.value = false;
});

function getTooltipContent(dataString: string) {
  if (!dataString) return "";
  return dataString.length <= props.truncateLength ? "" : dataString;
}

function shouldLoadMore() {
  return (props.totalRecords ?? 0) > props.data.length;
}

function shouldHandleLoad() {
  return shouldLoadMore() && !props.loading;
}

async function loadMore() {
  if (shouldHandleLoad() && props.loadMoreFnc) {
    localLoading.value = true;
    page.value++;
    try {
      await props.loadMoreFnc(page.value);
    } finally {
      localLoading.value = false;
    }
  }
}
</script>

<style lang="scss">
.t-autocomplete-list {
  .ps__rail-y {
    right: 0 !important;
    left: auto !important;
  }
}
</style>
